import React, { useState, useEffect, useCallback } from 'react';
import { Typography, Container, Paper, Box, Button, Card, CardContent, CardActions, Alert, Skeleton, ButtonBase, IconButton, Snackbar, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Link } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import AssessmentIcon from '@mui/icons-material/Assessment';
import InsightsIcon from '@mui/icons-material/Insights';
import HistoryIcon from '@mui/icons-material/History';
import { useAuth } from '../context/AuthContext';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { useSession } from '../context/SessionContext';
import { useWorkspace } from '../context/WorkspaceContext';
import { getShapeUpSyncStatus, syncCycleScopes } from '../utils/shapeUpApi';
import JiraSyncStatus from '../components/shapeup/JiraSyncStatus';

const DashboardPage = () => {
  const { user } = useAuth();
  const { prepareFacilitatorSession, loading: sessionLoading, error: sessionError, clearSessionError } = useSession();
  const { currentOrganization } = useWorkspace();
  const navigate = useNavigate();
  const [mySessions, setMySessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [error, setError] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null);
  // Cycle-wide Jira freshness for the current org, shown only when Jira is on and
  // there is a current cycle. Absent for non-Jira orgs, so the dashboard is
  // unchanged for them.
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncingCycle, setSyncingCycle] = useState(false);

  const fetchSessions = useCallback(async () => {
    setLoadingSessions(true);
    setError(null);
    setDeleteError(null);
    try {
      const response = await api.get('/sessions/facilitator/mine');
      setMySessions(response.data);
    } catch (err) {
      console.error('Error fetching sessions:', err);
      setError(err.response?.data?.message || 'Failed to load sessions.');
    }
    setLoadingSessions(false);
  }, []);

  useEffect(() => {
    if (user) {
      fetchSessions();
    }
  }, [user, fetchSessions]);

  const loadSyncStatus = useCallback(async () => {
    if (!currentOrganization?.id) {
      setSyncStatus(null);
      return;
    }
    try {
      const data = await getShapeUpSyncStatus(currentOrganization.id);
      setSyncStatus(data);
    } catch {
      // A failed freshness check is not worth an error banner; just hide the
      // badge rather than making the dashboard look broken.
      setSyncStatus(null);
    }
  }, [currentOrganization?.id]);

  useEffect(() => { loadSyncStatus(); }, [loadSyncStatus]);

  const handleRefreshCycleSync = async () => {
    if (!syncStatus?.cycle?.id) return;
    setSyncingCycle(true);
    try {
      await syncCycleScopes(syncStatus.cycle.id);
      await loadSyncStatus();
      setSuccessMessage('Synced from Jira.');
    } catch (err) {
      setError(err.response?.data?.message || 'Could not sync from Jira.');
    } finally {
      setSyncingCycle(false);
    }
  };

  const handleDeleteClick = (sessionId, event) => {
    event.stopPropagation();
    setSessionToDelete(sessionId);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSessionToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!sessionToDelete) return;
    setDeleteError(null);
    setSuccessMessage(null);
    try {
      await api.delete(`/sessions/${sessionToDelete}`);
      setSuccessMessage('Session deleted successfully.');
      fetchSessions();
    } catch (err) {
      console.error(`Error deleting session ${sessionToDelete}:`, err);
      setDeleteError(err.response?.data?.message || 'Failed to delete session.');
    }
    handleCloseDialog();
  };

  const handleCloseSnackbar = () => {
    setSuccessMessage(null);
    setDeleteError(null);
    setError(null);
    if (sessionError) clearSessionError();
  };

  const renderSkeletons = () => (
    <Box>
      {[...Array(2)].map((_, index) => (
        <Card key={index} sx={{ mb: 2 }}>
          <CardContent>
            <Skeleton variant="text" width="60%" height={32} />
            <Skeleton variant="text" width="40%" height={20} sx={{ mt: 1 }} />
            <Skeleton variant="text" width="30%" height={20} />
          </CardContent>
          <CardActions>
            <Skeleton variant="rectangular" width={100} height={30} />
          </CardActions>
        </Card>
      ))}
    </Box>
  );

  const handleOpenSession = async (sessionId) => {
    console.log(`[DashboardPage] handleOpenSession called for sessionId: ${sessionId}`);
    console.log(`[DashboardPage] User:`, user);
    console.log(`[DashboardPage] Will navigate to: /meeting/${sessionId}`);
    
    if (!user || !user.id) {
      console.error("DashboardPage: User not available for preparing session.");
      setError("User information is missing. Please try logging in again."); 
      return;
    }
    
    setError(null); 
    
    console.log(`[DashboardPage] Calling prepareFacilitatorSession for sessionId: ${sessionId}, userId: ${user.id}`);
    const success = await prepareFacilitatorSession(sessionId, user.id);
    console.log(`[DashboardPage] prepareFacilitatorSession result:`, success);
    
    if (success) {
      console.log(`[DashboardPage] Success! Navigating to /meeting/${sessionId}`);
      navigate(`/meeting/${sessionId}`);
    } else {
      console.error("DashboardPage: Failed to prepare session for facilitator.");
    }
  };

  return (
    <Container maxWidth="lg">
      <Paper elevation={3} sx={{ p: 3, mt: 4 }}>
        <Typography variant="h4" gutterBottom>
          Welcome to your Dashboard, {user?.name || user?.email}!
        </Typography>
        <Typography variant="body1" paragraph>
          Manage your planning poker sessions from here.
        </Typography>
        <Box sx={{ mt: 3, mb: 4, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            color="primary"
            component={RouterLink}
            to="/create-session"
            startIcon={<AddCircleOutlineIcon />}
            sx={{ py: 1.5, px: 3 }}
          >
            Create New Session
          </Button>
          <Button
            variant="outlined"
            color="primary"
            component={RouterLink}
            to="/analytics"
            startIcon={<InsightsIcon />}
            sx={{ py: 1.5, px: 3 }}
          >
            View Analytics
          </Button>
          {syncStatus?.jiraEnabled && syncStatus.cycle && (
            <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
              <Link
                component={RouterLink}
                to="/shape-up"
                underline="hover"
                sx={{ mr: 1, whiteSpace: 'nowrap' }}
              >
                {syncStatus.cycle.name}
              </Link>
              <JiraSyncStatus
                lastSyncedAt={syncStatus.lastSyncedAt}
                failing={syncStatus.failing}
                syncing={syncingCycle}
                canRefresh={syncStatus.canRefresh}
                onRefresh={handleRefreshCycleSync}
              />
            </Box>
          )}
        </Box>
        
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom>
            My Sessions
          </Typography>
          {loadingSessions ? (
            renderSkeletons()
          ) : error ? (
            <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>
          ) : sessionError ? (
            <Alert severity="error" sx={{ my: 2 }}>{sessionError}</Alert>
          ) : mySessions.length > 0 ? (
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: theme => theme.spacing(2),
            }}>
              {mySessions.map((session) => (
                <Card 
                  key={session.sessionId} 
                  elevation={2}
                  sx={{
                    mb: 2,
                    height: '100%',
                    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 8,
                    },
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                  }}
                >
                  <ButtonBase 
                    onClick={() => handleOpenSession(session.sessionId)}
                    disabled={sessionLoading}
                    sx={{ 
                      width: '100%', 
                      textAlign: 'left', 
                      p: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'stretch',
                      flexGrow: 1,
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                      <Typography variant="h6">{session.name || 'Unnamed Session'}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Session ID: {session.sessionId}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Created: {new Date(session.createdAt).toLocaleDateString()}
                      </Typography>
                    </CardContent>
                  </ButtonBase>
                  <CardActions sx={{ justifyContent: 'space-between', alignItems: 'center', pt: 0, px: 1, pb: 1 }}>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Button
                        variant="text"
                        size="small"
                        component={RouterLink}
                        to={`/session/${session.sessionId}/reflections`}
                        startIcon={<AssessmentIcon />}
                        aria-label="View reflections for this session"
                      >
                        Reflections
                      </Button>
                      <Button
                        variant="text"
                        size="small"
                        component={RouterLink}
                        to={`/session/${session.sessionId}/retro`}
                        startIcon={<HistoryIcon />}
                        aria-label="View retro for this session"
                      >
                        Retro
                      </Button>
                    </Box>
                    <IconButton 
                      aria-label="delete session"
                      onClick={(e) => handleDeleteClick(session.sessionId, e)}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </CardActions>
                </Card>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              You haven't created any sessions yet.
            </Typography>
          )}
        </Box>
      </Paper>
      <Snackbar 
        open={!!successMessage || !!deleteError || !!error || !!sessionError}
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
            onClose={handleCloseSnackbar} 
            severity={successMessage ? "success" : "error"} 
            sx={{ width: '100%' }}
        >
          {successMessage || deleteError || error || sessionError}
        </Alert>
      </Snackbar>
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        aria-labelledby="alert-dialog-title-dashboard"
        aria-describedby="alert-dialog-description-dashboard"
      >
        <DialogTitle id="alert-dialog-title-dashboard">
          {"Confirm Delete"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description-dashboard">
            Are you sure you want to delete this session? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default DashboardPage; 